import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { InvitationService, PublicAccessService } from '../services/invitationService';
import type { InvitationToken, InvitationWithCreator } from '../types';

/**
 * 管理者招待を作成するhook
 */
export const useCreateInvitation = () => {
  const queryClient = useQueryClient();
  const { user, profile } = useAuth();

  return useMutation({
    mutationFn: async (email: string) => {
      if (!user?.id || !profile?.organization_id) {
        throw new Error('ユーザー情報が取得できません');
      }
      return InvitationService.createInvitation(
        email,
        profile.organization_id,
        user.id
      );
    },
    onSuccess: () => {
      // 招待一覧のキャッシュを無効化
      queryClient.invalidateQueries({
        queryKey: ['invitations', profile?.organization_id],
      });
    },
    onError: (error: Error) => {
      console.error('Failed to create invitation:', error);
    },
  });
};

/**
 * 組織の招待一覧を取得するhook
 */
export const useInvitations = () => {
  const { user, profile } = useAuth();

  return useQuery<InvitationWithCreator[]>({
    queryKey: ['invitations', profile?.organization_id],
    queryFn: async () => {
      if (!user?.id || !profile?.organization_id) {
        throw new Error('ユーザー情報が取得できません');
      }
      return InvitationService.getOrganizationInvitations(
        profile.organization_id,
        user.id
      );
    },
    enabled: !!user?.id && !!profile?.organization_id,
  });
};

/**
 * 招待トークンを検証するhook
 */
export const useValidateInvitation = (token: string | null) => {
  return useQuery({
    queryKey: ['validate-invitation', token],
    queryFn: async () => {
      if (!token) throw new Error('トークンが必要です');
      return InvitationService.validateInvitationToken(token);
    },
    enabled: !!token,
    retry: false, // トークン検証は再試行しない
    staleTime: 0, // 常に最新の検証結果を取得
  });
};

/**
 * 招待を受諾するhook
 */
export const useAcceptInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ token, userId }: { token: string; userId: string }) => {
      return InvitationService.acceptInvitation(token, userId);
    },
    onSuccess: () => {
      // ユーザーの権限情報を再取得
      queryClient.invalidateQueries({ queryKey: ['user-permissions'] });
      queryClient.invalidateQueries({ queryKey: ['user-organizations'] });
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
    },
  });
};

/**
 * 招待をキャンセルするhook
 */
export const useCancelInvitation = () => {
  const queryClient = useQueryClient();
  const { user, profile } = useAuth();

  return useMutation({
    mutationFn: async (invitationId: string) => {
      if (!user?.id) {
        throw new Error('ユーザー情報が取得できません');
      }
      return InvitationService.cancelInvitation(invitationId, user.id);
    },
    onSuccess: () => {
      // 招待一覧のキャッシュを無効化
      queryClient.invalidateQueries({
        queryKey: ['invitations', profile?.organization_id],
      });
    },
  });
};

/**
 * 公開アクセストークンを生成するhook
 */
export const useGeneratePublicToken = () => {
  const queryClient = useQueryClient();
  const { user, profile } = useAuth();

  return useMutation({
    mutationFn: async () => {
      if (!user?.id || !profile?.organization_id) {
        throw new Error('ユーザー情報が取得できません');
      }
      return PublicAccessService.generatePublicToken(
        profile.organization_id,
        user.id
      );
    },
    onSuccess: () => {
      // 公開トークンのキャッシュを無効化
      queryClient.invalidateQueries({
        queryKey: ['public-token', profile?.organization_id],
      });
    },
  });
};

/**
 * 現在の公開トークンを取得するhook
 */
export const useCurrentPublicToken = () => {
  const { user, profile } = useAuth();

  return useQuery({
    queryKey: ['public-token', profile?.organization_id],
    queryFn: async () => {
      if (!user?.id || !profile?.organization_id) {
        throw new Error('ユーザー情報が取得できません');
      }
      return PublicAccessService.getCurrentPublicToken(
        profile.organization_id,
        user.id
      );
    },
    enabled: !!user?.id && !!profile?.organization_id,
  });
};

/**
 * 招待URLをクリップボードにコピーするユーティリティhook
 */
export const useCopyInviteUrl = () => {
  return {
    copyInviteUrl: async (token: string): Promise<boolean> => {
      const url = InvitationService.generateInviteUrl(token);
      try {
        await navigator.clipboard.writeText(url);
        return true;
      } catch (error) {
        console.error('Failed to copy to clipboard:', error);
        return false;
      }
    },
    copyPublicUrl: async (token: string): Promise<boolean> => {
      const url = PublicAccessService.generatePublicUrl(token);
      try {
        await navigator.clipboard.writeText(url);
        return true;
      } catch (error) {
        console.error('Failed to copy to clipboard:', error);
        return false;
      }
    },
  };
};

/**
 * メール送信機能のhook（プレースホルダー）
 */
export const useSendInvitationEmail = () => {
  return useMutation({
    mutationFn: async ({
      email,
      invitationUrl,
      organizationName,
    }: {
      email: string;
      invitationUrl: string;
      organizationName: string;
    }) => {
      return InvitationService.sendInvitationEmail(
        email,
        invitationUrl,
        organizationName
      );
    },
  });
};